import React, { useState } from "react";
import SimpleBar from 'simplebar-react';
import { useLocation } from "react-router-dom";
import { CSSTransition } from 'react-transition-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import { Nav, Image, Button, Accordion, Navbar, Badge } from '@themesberg/react-bootstrap';
import { Link } from 'react-router-dom';

import { Routes } from "../routes";
import { useAuth } from "../contexts/AuthContext";
import { useRole } from "../contexts/RoleContext";
import ReactHero from "../assets/img/technologies/react-hero-logo.svg";
import ProfilePicture from "../assets/img/team/profile.jpg";

import { CoordinatorMenuItems } from "./sidebars/CoordinatorSidebar";
import { PrincipalMenuItems } from "./sidebars/PrincipalSidebar";
import { TeacherMenuItems } from "./sidebars/TeacherSidebar";
import { MunicipalSecretaryMenuItems } from "./sidebars/MunicipalSecretarySidebar";

const RoleSidebar = (props = {}) => {
  const location = useLocation();
  const { pathname } = location;
  const { user, logout } = useAuth();
  const { selectedRole } = useRole();
  const [show, setShow] = useState(false);
  const showClass = show ? "show" : "";

  const onCollapse = () => setShow(!show);

  const CollapsableNavItem = (props) => {
    const { eventKey, title, icon, children = null } = props;
    const defaultKey = pathname.indexOf(eventKey) !== -1 ? eventKey : "";

    return (
      <Accordion as={Nav.Item} defaultActiveKey={defaultKey}>
        <Accordion.Item eventKey={eventKey}>
          <Accordion.Button as={Nav.Link} className="d-flex justify-content-between align-items-center">
            <span>
              <span className="sidebar-icon"><FontAwesomeIcon icon={icon} /> </span>
              <span className="sidebar-text">{title}</span>
            </span>
          </Accordion.Button>
          <Accordion.Body className="multi-level">
            <Nav className="flex-column">
              {children}
            </Nav>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  };

  const NavItem = (props) => {
    const { title, link, external, target, icon, image, badgeText, badgeBg = "secondary", badgeColor = "primary" } = props;
    const classNames = badgeText ? "d-flex justify-content-start align-items-center justify-content-between" : "";
    const navItemClassName = link === pathname ? "active" : "";
    const linkProps = external ? { href: link } : { as: Link, to: link };

    return (
      <Nav.Item className={navItemClassName} onClick={() => setShow(false)}>
        <Nav.Link {...linkProps} target={target} className={classNames}>
          <span>
            {icon ? <span className="sidebar-icon"><FontAwesomeIcon icon={icon} /> </span> : null}
            {image ? <Image src={image} width={20} height={20} className="sidebar-icon svg-icon" /> : null}

            <span className="sidebar-text">{title}</span>
          </span>
          {badgeText ? (
            <Badge pill bg={badgeBg} text={badgeColor} className="badge-md notification-count ms-2">{badgeText}</Badge>
          ) : null}
        </Nav.Link>
      </Nav.Item>
    );
  };

  const getUserRole = () => {
    // Se há um papel selecionado no RoleContext, usar ele
    if (selectedRole) {
      return selectedRole;
    }

    // Fallback: buscar primeiro papel ativo (caso selectedRole ainda não esteja definido)
    if (!user) return null;

    if (user.municipality_users && user.municipality_users.length > 0) {
      const activeMunicipality = user.municipality_users.find(mu => mu.active);
      if (activeMunicipality) {
        return 'MUNICIPAL_SECRETARY';
      }
    }

    if (user.school_users && user.school_users.length > 0) {
      const activeSchoolUser = user.school_users.find(su => su.active);
      if (activeSchoolUser) {
        return activeSchoolUser.role;
      }
    }

    return null;
  };

  const renderMenuItems = () => {
    const role = getUserRole();

    switch (role) {
      case 'TEACHER':
        return <TeacherMenuItems NavItem={NavItem} CollapsableNavItem={CollapsableNavItem} />;
      case 'COORD':
        return <CoordinatorMenuItems NavItem={NavItem} CollapsableNavItem={CollapsableNavItem} />;
      case 'PRINCIPAL':
        return <PrincipalMenuItems NavItem={NavItem} CollapsableNavItem={CollapsableNavItem} />;
      case 'MUNICIPAL_SECRETARY':
        return <MunicipalSecretaryMenuItems NavItem={NavItem} CollapsableNavItem={CollapsableNavItem} />;
      default:
        return (
          <NavItem
            title="Dashboard"
            link={Routes.DashboardOverview.path}
            icon={null}
          />
        );
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <>
      <Navbar expand={false} collapseOnSelect variant="dark" className="navbar-theme-primary px-4 d-md-none">
        <Navbar.Brand className="me-lg-5" as={Link} to={Routes.DashboardOverview.path}>
          <Image src={ReactHero} className="navbar-brand-light" />
        </Navbar.Brand>
        <Navbar.Toggle as={Button} aria-controls="main-navbar" onClick={onCollapse}>
          <span className="navbar-toggler-icon" />
        </Navbar.Toggle>
      </Navbar>
      <CSSTransition timeout={300} in={show} classNames="sidebar-transition">
        <SimpleBar className={`collapse ${showClass} sidebar d-md-block bg-primary text-white`}>
          <div className="sidebar-inner px-4 pt-3">
            <div className="user-card d-flex d-md-none align-items-center justify-content-between justify-content-md-center pb-4">
              <div className="d-flex align-items-center">
                <div className="user-avatar lg-avatar me-4">
                  <Image src={ProfilePicture} className="card-img-top rounded-circle border-white" />
                </div>
                <div className="d-block">
                  <h6>{user?.name || 'Usuário'}</h6>
                  <Button variant="secondary" size="xs" onClick={handleLogout} className="text-dark">
                    <FontAwesomeIcon icon={faSignOutAlt} className="me-2" /> Sair
                  </Button>
                </div>
              </div>
              <Nav.Link className="collapse-close d-md-none" onClick={onCollapse}>
                <FontAwesomeIcon icon={faTimes} />
              </Nav.Link>
            </div>
            <Nav className="flex-column pt-3 pt-md-0">
              <NavItem title="SGE Inovare" link={Routes.DashboardOverview.path} image={ReactHero} />

              {renderMenuItems()}
            </Nav>
          </div>
        </SimpleBar>
      </CSSTransition>
    </>
  );
};

export default RoleSidebar;
